import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle, CheckCircle2, Clock3, Loader2, MailPlus,
  RefreshCcw, X, ShieldAlert, Mail, MessageSquare, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LoadingScreen from '../components/ui/LoadingScreen';
import { roleService } from '../services/roleService';
import { tenantService } from '../services/tenantService';
import { showError, showSuccess } from '../components/Alerts';
import { InvitationAdminResponse, InvitationStatus, RoleWithPermissions } from '../types';
import { cn } from '../lib/utils';

// ── Status meta ────────────────────────────────────────────────────────────

const STATUS_META: Record<InvitationStatus, { label: string; className: string; icon: React.ReactNode }> = {
  [InvitationStatus.PENDING]: {
    label: 'Pendiente',
    className: 'border-amber-200 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    icon: <Clock3 className="w-3 h-3" />,
  },
  [InvitationStatus.ACCEPTED]: {
    label: 'Aceptada',
    className: 'border-emerald-200 bg-emerald-100 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
    icon: <CheckCircle2 className="w-3 h-3" />,
  },
  [InvitationStatus.REJECTED]: {
    label: 'Rechazada',
    className: 'border-rose-200 bg-rose-100 text-rose-800 dark:border-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
    icon: <AlertCircle className="w-3 h-3" />,
  },
  [InvitationStatus.EXPIRED]: {
    label: 'Vencida',
    className: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300',
    icon: <ShieldAlert className="w-3 h-3" />,
  },
  [InvitationStatus.CANCELLED]: {
    label: 'Cancelada',
    className: 'border-zinc-200 bg-zinc-100 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
    icon: <X className="w-3 h-3" />,
  },
  [InvitationStatus.SUPERSEDED]: {
    label: 'Reemplazada',
    className: 'border-violet-200 bg-violet-100 text-violet-800 dark:border-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
    icon: <RefreshCcw className="w-3 h-3" />,
  },
  [InvitationStatus.INVALIDATED_BY_REGISTRATION]: {
    label: 'Invalidada',
    className: 'border-orange-200 bg-orange-100 text-orange-800 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
    icon: <AlertCircle className="w-3 h-3" />,
  },
};

const STATUS_FILTER_OPTIONS: Array<{ value: 'all' | InvitationStatus; label: string }> = [
  { value: 'all', label: 'Todos los estados' },
  { value: InvitationStatus.PENDING, label: 'Pendientes' },
  { value: InvitationStatus.ACCEPTED, label: 'Aceptadas' },
  { value: InvitationStatus.EXPIRED, label: 'Vencidas' },
  { value: InvitationStatus.CANCELLED, label: 'Canceladas' },
];

const getStatusMeta = (status: InvitationStatus) =>
  STATUS_META[status] ?? {
    label: status,
    className: 'border-border bg-muted text-muted-foreground',
    icon: <AlertCircle className="w-3 h-3" />,
  };

const canResend = (s: InvitationStatus) =>
  new Set<InvitationStatus>([
    InvitationStatus.PENDING, InvitationStatus.EXPIRED,
    InvitationStatus.CANCELLED, InvitationStatus.REJECTED,
  ]).has(s);

const canCancel = (s: InvitationStatus) => s === InvitationStatus.PENDING;

// ── Formatters ─────────────────────────────────────────────────────────────

const dateFormatter = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : dateFormatter.format(d);
};
const sortDesc = (items: InvitationAdminResponse[]) =>
  [...items].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

// ── Types ──────────────────────────────────────────────────────────────────

type ConfirmAction = 'cancel' | 'resend';
type ConfirmState = { invitation: InvitationAdminResponse; action: ConfirmAction } | null;

const ROWS_PER_PAGE = 10;

function formatRoleName(name: string): string {
  if (name === 'SIN_ASIGNAR') return 'Sin asignar';
  const lower = name.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

// ── Stat pill ──────────────────────────────────────────────────────────────

function StatPill({ label, value, highlight }: { label: string; value: number; highlight?: string }) {
  return (
    <div className="flex flex-col items-center px-5 py-3 rounded-xl border border-border bg-background min-w-[90px]">
      <span className={cn('text-2xl font-bold', highlight ?? 'text-foreground')}>{value}</span>
      <span className="text-xs text-muted-foreground mt-0.5 text-center leading-tight">{label}</span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

const InvitationsPage: React.FC = () => {
  const [roles, setRoles] = useState<RoleWithPermissions[]>([]);
  const [invitations, setInvitations] = useState<InvitationAdminResponse[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | InvitationStatus>('all');
  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({ email: '', roleId: '', personalMessage: '' });

  const availableRoles = useMemo(() =>
    [...roles]
      .filter(r => r.isActive)
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })),
    [roles]
  );

  const roleNameById = useMemo(() => new Map(roles.map(r => [r.id, r.name])), [roles]);
  const invitationsSorted = useMemo(() => sortDesc(invitations), [invitations]);

  const summary = useMemo(() => ({
    total: invitationsSorted.length,
    pending: invitationsSorted.filter(i => i.status === InvitationStatus.PENDING).length,
    accepted: invitationsSorted.filter(i => i.status === InvitationStatus.ACCEPTED).length,
    closed: invitationsSorted.filter(i =>
      [InvitationStatus.EXPIRED, InvitationStatus.CANCELLED, InvitationStatus.REJECTED].includes(i.status)
    ).length,
  }), [invitationsSorted]);

  // ── Data loading ───────────────────────────────────────────────────────

  const loadRoles = async () => {
    setRolesLoading(true);
    try { setRoles(await roleService.getAll()); }
    catch (error: any) { showError(error?.response?.data?.message || 'No se pudieron cargar los roles'); }
    finally { setRolesLoading(false); }
  };

  useEffect(() => { void loadRoles(); }, []);

  const loadInvitations = useCallback(async (signal?: AbortSignal) => {
    setInvitationsLoading(true);
    try {
      setInvitations(sortDesc(
        await tenantService.getInvitations(statusFilter === 'all' ? undefined : statusFilter)
      ));
    } catch (err: any) {
      // Ignorar errores de cancelación (AbortController / StrictMode double-mount)
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      showError('No se pudieron cargar las invitaciones');
    } finally {
      setInvitationsLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    const controller = new AbortController();
    void loadInvitations(controller.signal);
    setPage(1);
    return () => controller.abort();
  }, [loadInvitations]);

  useEffect(() => {
    if (availableRoles.length === 0) { setForm(c => c.roleId ? { ...c, roleId: '' } : c); return; }
    setForm(c => {
      const exists = availableRoles.some(r => String(r.id) === c.roleId);
      return exists && c.roleId ? c : { ...c, roleId: String(availableRoles[0].id) };
    });
  }, [availableRoles]);

  // ── Pagination ─────────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(invitationsSorted.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageRows = invitationsSorted.slice((safePage - 1) * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE);

  // ── Handlers ──────────────────────────────────────────────────────────

  const resetForm = () => setForm(c => ({ ...c, email: '', personalMessage: '' }));

  const handleInviteSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = form.email.trim();
    const roleId = Number(form.roleId);
    if (!email) { showError('El email es obligatorio'); return; }
    if (!emailPattern.test(email)) { showError('Ingresá un email válido'); return; }
    if (!form.roleId || Number.isNaN(roleId)) { showError('El rol es obligatorio'); return; }

    setSendingInvite(true);
    try {
      const payload = {
        email,
        roleId,
        ...(form.personalMessage.trim() ? { personalMessage: form.personalMessage.trim() } : {}),
      };
      const invitation = await tenantService.inviteUser(payload);
      showSuccess(`Invitación enviada a ${invitation.email}`);
      resetForm();
      setShowInviteDialog(false);
      await loadInvitations();
    } catch (error: any) {
      showError(error?.response?.data?.message || 'No se pudo enviar la invitación');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmState) return;
    const { invitation, action } = confirmState;
    setActionLoadingId(invitation.id);
    try {
      if (action === 'cancel') {
        await tenantService.cancelInvitation(invitation.id);
        showSuccess(`Invitación cancelada para ${invitation.email}`);
      } else {
        await tenantService.resendInvitation(invitation.id);
        showSuccess(`Invitación reenviada a ${invitation.email}`);
      }
      setConfirmState(null);
      await loadInvitations();
    } catch (error: any) {
      showError(error?.response?.data?.message || 'No se pudo completar la acción');
    } finally {
      setActionLoadingId(null);
    }
  };

  const getRoleLabel = (roleId: number) => {
    const name = roleNameById.get(roleId);
    return name ? formatRoleName(name) : `Rol #${roleId}`;
  };

  if (invitationsLoading && invitations.length === 0 && rolesLoading) {
    return <LoadingScreen message="Cargando invitaciones" fullScreen={false} />;
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="w-full h-full p-6 space-y-6">

      {/* ── Page header ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center flex-shrink-0 bg-emerald-100 w-11 h-11 rounded-xl dark:bg-emerald-900/30">
            <Mail className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Invitaciones</h1>
            <p className="text-sm text-muted-foreground">Gestioná el acceso de nuevos miembros a la empresa.</p>
          </div>
        </div>
        <Button
          onClick={() => setShowInviteDialog(true)}
          className="flex items-center gap-2 rounded-lg"
          disabled={rolesLoading}
        >
          <MailPlus className="w-4 h-4" />
          Nueva invitación
        </Button>
      </div>

      {/* ── Stats + filter row ── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <StatPill label="Total" value={summary.total} />
          <StatPill label="Pendientes" value={summary.pending} highlight="text-amber-600 dark:text-amber-400" />
          <StatPill label="Aceptadas" value={summary.accepted} highlight="text-emerald-600 dark:text-emerald-400" />
          <StatPill label="Cerradas" value={summary.closed} highlight="text-muted-foreground" />
        </div>
        <div className="w-full sm:w-52">
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v as 'all' | InvitationStatus); }}>
            <SelectTrigger className="rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0">
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Historial table ── */}
      <Card>
        <CardHeader className="pb-4 border-b border-border">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-foreground">Historial</h2>
              <p className="mt-0.5 text-sm text-muted-foreground">Más recientes primero.</p>
            </div>
            {invitationsLoading && invitationsSorted.length > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" />
                Actualizando
              </span>
            )}
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Destinatario</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Estado</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Rol asignado</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Creada</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Expira</th>
                  <th className="px-5 py-3 text-xs font-semibold tracking-wide text-left uppercase text-muted-foreground">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <div className="flex items-center justify-center w-10 h-10 mx-auto mb-3 rounded-xl bg-muted text-muted-foreground">
                        <MailPlus className="w-5 h-5" />
                      </div>
                      <p className="text-sm font-medium text-foreground">Sin invitaciones</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Cambiá el filtro o enviá una nueva invitación.
                      </p>
                    </td>
                  </tr>
                ) : (
                  pageRows.map(invitation => {
                    const meta = getStatusMeta(invitation.status);
                    const loading = actionLoadingId === invitation.id;

                    return (
                      <tr key={invitation.id} className="transition-colors hover:bg-muted/30 group">
                        {/* Destinatario */}
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-foreground truncate max-w-[220px]">{invitation.email}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">#{invitation.id}</p>
                        </td>

                        {/* Estado */}
                        <td className="px-5 py-3.5">
                          <Badge variant="outline" className={cn('gap-1 text-xs font-medium', meta.className)}>
                            {meta.icon}
                            {meta.label}
                          </Badge>
                        </td>

                        {/* Rol */}
                        <td className="px-5 py-3.5 text-sm text-foreground whitespace-nowrap">
                          {getRoleLabel(invitation.roleId)}
                        </td>

                        {/* Creada */}
                        <td className="px-5 py-3.5 text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(invitation.createdAt)}
                        </td>

                        {/* Expira */}
                        <td className="px-5 py-3.5 text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(invitation.expiresAt)}
                        </td>

                        {/* Acciones */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            {canCancel(invitation.status) && (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="rounded-lg border-rose-200 text-rose-700 hover:bg-rose-50 dark:border-rose-900/60 dark:text-rose-300 dark:hover:bg-rose-900/20"
                                onClick={() => setConfirmState({ invitation, action: 'cancel' })}
                                disabled={loading}
                              >
                                <X className="mr-1.5 h-3.5 w-3.5" />
                                Cancelar
                              </Button>
                            )}
                            {canResend(invitation.status) && (
                              <Button
                                type="button"
                                size="sm"
                                variant={invitation.status === InvitationStatus.PENDING ? 'default' : 'outline'}
                                className="rounded-lg"
                                onClick={() => setConfirmState({ invitation, action: 'resend' })}
                                disabled={loading}
                              >
                                <RefreshCcw className="mr-1.5 h-3.5 w-3.5" />
                                Reenviar
                              </Button>
                            )}
                            {!canCancel(invitation.status) && !canResend(invitation.status) && (
                              <span className="text-xs italic text-muted-foreground/50">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>

        {invitationsSorted.length > ROWS_PER_PAGE && (
          <CardFooter className="flex items-center justify-between px-5 py-3 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Página <span className="font-medium text-foreground">{safePage}</span> de{' '}
              <span className="font-medium text-foreground">{totalPages}</span>
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Anterior
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="flex items-center gap-1"
              >
                Siguiente <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardFooter>
        )}
      </Card>

      {/* ── Nueva invitación dialog ── */}
      <Dialog open={showInviteDialog} onOpenChange={open => { if (!open) { setShowInviteDialog(false); resetForm(); } }}>
        <DialogContent className="sm:max-w-md rounded-xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Nueva invitación</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              El destinatario recibirá un email con un enlace para registrarse.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleInviteSubmit} className="pt-1 space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold tracking-wide uppercase text-muted-foreground" htmlFor="inv-email">
                Email del destinatario
              </label>
              <Input
                id="inv-email"
                type="email"
                value={form.email}
                onChange={e => setForm(c => ({ ...c, email: e.target.value }))}
                placeholder="nombre@empresa.com"
                autoComplete="email"
                className="rounded-lg"
              />
            </div>

            {/* Rol */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold tracking-wide uppercase text-muted-foreground" htmlFor="inv-role">
                Rol asignado
              </label>
              <Select
                value={form.roleId}
                onValueChange={v => setForm(c => ({ ...c, roleId: v }))}
                disabled={rolesLoading || availableRoles.length === 0}
              >
                <SelectTrigger id="inv-role" className="rounded-lg">
                  <SelectValue placeholder={availableRoles.length > 0 ? 'Seleccioná un rol' : 'Sin roles disponibles'} />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map(role => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      {formatRoleName(role.name)}
                      {role.description && (
                        <span className="ml-1 text-xs text-muted-foreground">— {role.description}</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Mensaje personalizado */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5" htmlFor="inv-msg">
                <MessageSquare className="h-3.5 w-3.5" />
                Mensaje personalizado
                <span className="font-normal tracking-normal normal-case text-muted-foreground/70">(opcional)</span>
              </label>
              <textarea
                id="inv-msg"
                rows={3}
                value={form.personalMessage}
                onChange={e => setForm(c => ({ ...c, personalMessage: e.target.value }))}
                placeholder="Ej: Hola! Te invitamos a unirte al equipo de operaciones…"
                maxLength={400}
                className={cn(
                  'w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm',
                  'text-foreground placeholder:text-muted-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              />
              <p className="text-xs text-right text-muted-foreground">
                {form.personalMessage.length}/400
              </p>
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowInviteDialog(false); resetForm(); }}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={sendingInvite || rolesLoading || availableRoles.length === 0}
                className="flex items-center gap-2"
              >
                {sendingInvite ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Enviando…</>
                ) : (
                  <><MailPlus className="w-4 h-4" />Enviar invitación</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Confirm dialog ── */}
      <Dialog open={confirmState !== null} onOpenChange={open => { if (!open) setConfirmState(null); }}>
        <DialogContent className="sm:max-w-sm rounded-xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              {confirmState?.action === 'cancel' ? 'Cancelar invitación' : 'Reenviar invitación'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {confirmState?.action === 'cancel'
                ? 'El enlace actual quedará invalidado y no podrá usarse.'
                : 'Se enviará un nuevo correo con un enlace actualizado.'}
            </DialogDescription>
          </DialogHeader>

          {confirmState && (
            <div className="px-4 py-3 text-sm border rounded-lg border-border bg-muted/30">
              <p className="font-medium truncate text-foreground">{confirmState.invitation.email}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Estado: {getStatusMeta(confirmState.invitation.status).label}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setConfirmState(null)} disabled={actionLoadingId !== null}>
              Volver
            </Button>
            <Button
              type="button"
              variant={confirmState?.action === 'cancel' ? 'destructive' : 'default'}
              onClick={() => void handleConfirmAction()}
              disabled={actionLoadingId !== null}
            >
              {actionLoadingId !== null ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando…</>
              ) : confirmState?.action === 'cancel' ? (
                'Cancelar invitación'
              ) : (
                'Confirmar reenvío'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvitationsPage;
